import type {Comment, Thread, ThreadSubject} from '@github-ui/diff-lines/conversation-types'
import {useSuspenseQuery, type QueryKey} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {reactFetch} from '@github-ui/verified-fetch'

type CommentAuthor = {
  avatarUrl: string
  login: string
}

type CommentPreviews = {
  author?: CommentAuthor | null
}

export type PreviewInfo = {
  threadId: string
  commentId: string
  path: string
  threadPreviewComments: CommentPreviews[]
  originalDiffPathUri?: string | null
  firstComment?: Comment
  subject?: ThreadSubject
}

export type ThreadPreview = Pick<Thread, 'isResolved' | 'line' | 'subjectType' | 'subject' | 'commentsData'> & {
  threadId: string
  isOutdated?: boolean
  commentId: string
  path: string
  //TODO: deprecate threadPreviewComments
  threadPreviewComments: CommentPreviews[]
  originalDiffPathUri?: string | null
  firstComment?: Comment
}

export type ThreadPreviewComp = {
  thread?: Thread | null
  previewInfo: PreviewInfo
}

export type ThreadPreviewsPayload = ThreadPreview[]
export type ThreadPreviewsCompPayload = ThreadPreviewComp[]

// Type guard to check if an item is ThreadPreviewComp
export function isThreadPreviewComp(item: unknown): item is ThreadPreviewComp {
  return item !== null && typeof item === 'object' && 'previewInfo' in item
}

// Transform ThreadPreview to ThreadPreviewComp
export function transformThreadPreviewToComp(thread: ThreadPreview): ThreadPreviewComp {
  return {
    thread: {
      id: thread.threadId,
      isResolved: thread.isResolved,
      isOutdated: thread.isOutdated,
      line: thread.line,
      subjectType: thread.subjectType,
      subject: thread.subject,
      commentsData: thread.commentsData,
    },
    previewInfo: {
      threadId: thread.threadId,
      commentId: thread.commentId,
      path: thread.path,
      threadPreviewComments: thread.threadPreviewComments,
      originalDiffPathUri: thread.originalDiffPathUri,
      firstComment: thread.firstComment,
      subject: thread.subject,
    },
  }
}

export function threadPreviewsQueryKey(pathName: string): QueryKey {
  return [PageData.threadPreviews, pathName]
}

function threadPreviewsApiUrl(pathName: string): string {
  return `${pathName}/page_data/${PageData.threadPreviews}`
}

function threadPreviewsPayload(pathName: string) {
  const apiURL = threadPreviewsApiUrl(pathName)
  const queryKey = threadPreviewsQueryKey(pathName)
  return {
    queryKey,
    queryFn: async (): Promise<ThreadPreviewsCompPayload> => {
      const result = await reactFetch(apiURL)
      if (!result.ok) throw new Error(`HTTP ${result.status}`)
      const json = await result.json()

      // Check if the response is ThreadPreviewsCompPayload
      if (json.length > 0 && !isThreadPreviewComp(json[0])) {
        return (json as ThreadPreviewsPayload).map(transformThreadPreviewToComp)
      }
      return json as ThreadPreviewsCompPayload
    },
  }
}

export function useThreadPreviewsPageData({
  pathName,
  initialData,
}: {
  pathName: string
  initialData?: ThreadPreviewsPayload | ThreadPreviewsCompPayload
}) {
  const {queryFn, queryKey} = threadPreviewsPayload(pathName)
  const transformedInitialData =
    initialData && initialData.length > 0 && !isThreadPreviewComp(initialData[0])
      ? (initialData as ThreadPreviewsPayload).map(transformThreadPreviewToComp)
      : (initialData as ThreadPreviewsCompPayload)
  return useSuspenseQuery<ThreadPreviewsCompPayload>({
    queryKey,
    queryFn,
    initialData: transformedInitialData,
    staleTime: Infinity,
  })
}
